// Fichier: src/pages/accounting/BalanceSheet.js
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Container, Typography, Paper, Table, TableHead, TableRow, TableCell,
  TableBody, TableContainer, IconButton, TextField, MenuItem, Chip, Grid, Divider
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { db } from '../../firebase';
import { collection, onSnapshot, query, where } from 'firebase/firestore';

// ── Regroupement des comptes classe 1-5 en rubriques de bilan ──────────
// Basé sur classNumber + normalSide + préfixe de code. Si tu ajoutes des
// comptes avec un schéma de numérotation différent, vérifie qu'ils
// tombent dans la bonne rubrique ci-dessous.
function bilanGroup(account) {
  const code = account.code || '';
  if (account.classNumber === 2) return 'immobilisations';
  if (account.classNumber === 3) return 'stocks';
  if (account.classNumber === 5) return 'tresorerie';
  if (account.classNumber === 4 && account.normalSide === 'debit') return 'creances';
  if (account.classNumber === 4 && account.normalSide === 'credit') return 'dettesCT';
  if (account.classNumber === 1) {
    if (code.startsWith('16')) return 'provisions';
    if (code.startsWith('17')) return 'dettesLT';
    return 'fondsSociaux';
  }
  return null; // classes 6/7 exclues du bilan (voir résultat de l'exercice)
}

const ACTIF_GROUPS = [
  { key: 'immobilisations', label: 'Actifs immobilisés' },
  { key: 'stocks', label: 'Stocks' },
  { key: 'creances', label: 'Créances à un an au plus' },
  { key: 'tresorerie', label: 'Placements de trésorerie et valeurs disponibles' }
];

const PASSIF_GROUPS = [
  { key: 'fondsSociaux', label: 'Fonds sociaux' },
  { key: 'provisions', label: 'Provisions pour risques et charges' },
  { key: 'dettesLT', label: "Dettes à plus d'un an" },
  { key: 'dettesCT', label: 'Dettes à un an au plus' }
];

const BalanceSheet = () => {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState(String(new Date().getFullYear()));

  useEffect(() => {
    const unsubAccounts = onSnapshot(collection(db, 'accounting_accounts'), (snap) => {
      setAccounts(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
    const unsubEntries = onSnapshot(
      query(collection(db, 'accounting_entries'), where('status', '==', 'validé')),
      (snap) => {
        setEntries(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoading(false);
      }
    );
    return () => {
      unsubAccounts();
      unsubEntries();
    };
  }, []);

  const years = useMemo(() => {
    const set = new Set(entries.map((e) => e.exerciceId).filter(Boolean));
    set.add(String(new Date().getFullYear()));
    return Array.from(set).sort().reverse();
  }, [entries]);

  const accountsById = useMemo(() => {
    const map = {};
    accounts.forEach((a) => { map[a.id] = a; });
    return map;
  }, [accounts]);

  const { actifRows, passifRows, totalActif, totalPassif, resultatExercice } = useMemo(() => {
    const yearEnd = new Date(Number(year), 11, 31, 23, 59, 59);

    // Soldes cumulés (classes 1-5) : toutes les écritures validées jusqu'à
    // la fin de l'exercice sélectionné, quelle que soit leur année.
    const cumulBalances = {};
    // Résultat de l'exercice (classes 6-7) : uniquement les écritures de
    // l'exercice sélectionné.
    let produits = 0;
    let charges = 0;

    entries.forEach((e) => {
      const entryDate = e.date?.toDate ? e.date.toDate() : new Date(e.date);
      const isWithinCumul = entryDate <= yearEnd;
      const isWithinYear = e.exerciceId === year;

      (e.lines || []).forEach((l) => {
        const account = accountsById[l.accountId];
        if (!account) return;
        const debit = Number(l.debit) || 0;
        const credit = Number(l.credit) || 0;

        if (account.classNumber <= 5 && isWithinCumul) {
          if (!cumulBalances[account.id]) cumulBalances[account.id] = 0;
          cumulBalances[account.id] += account.normalSide === 'credit' ? credit - debit : debit - credit;
        }
        if (account.classNumber === 7 && isWithinYear) produits += credit - debit;
        if (account.classNumber === 6 && isWithinYear) charges += debit - credit;
      });
    });

    const resultat = produits - charges;

    const buildRows = (groups) =>
      groups.map((g) => {
        const groupAccounts = accounts
          .filter((a) => bilanGroup(a) === g.key)
          .map((a) => ({ ...a, solde: cumulBalances[a.id] || 0 }))
          .filter((a) => Math.abs(a.solde) > 0.005);
        const total = groupAccounts.reduce((s, a) => s + a.solde, 0);
        return { ...g, accounts: groupAccounts, total };
      });

    const actif = buildRows(ACTIF_GROUPS);
    const passif = buildRows(PASSIF_GROUPS);

    // Injecte le résultat de l'exercice dans le groupe "Fonds sociaux"
    const fondsSociaux = passif.find((g) => g.key === 'fondsSociaux');
    if (fondsSociaux) {
      fondsSociaux.accounts.push({
        id: 'resultat-exercice',
        code: '',
        label: `Résultat de l'exercice ${year}`,
        solde: resultat
      });
      fondsSociaux.total += resultat;
    }

    const totalA = actif.reduce((s, g) => s + g.total, 0);
    const totalP = passif.reduce((s, g) => s + g.total, 0);

    return { actifRows: actif, passifRows: passif, totalActif: totalA, totalPassif: totalP, resultatExercice: resultat };
  }, [accounts, entries, accountsById, year]);

  const isBalanced = Math.abs(totalActif - totalPassif) < 0.01;

  const renderSection = (title, groups, total) => (
    <TableContainer component={Paper} sx={{ height: '100%' }}>
      <Box sx={{ p: 2, bgcolor: 'grey.100' }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>{title}</Typography>
      </Box>
      <Table size="small">
        <TableBody>
          {groups.map((g) => (
            g.accounts.length > 0 && (
              <React.Fragment key={g.key}>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell colSpan={2}><strong>{g.label}</strong></TableCell>
                </TableRow>
                {g.accounts.map((a) => (
                  <TableRow key={a.id}>
                    <TableCell sx={{ pl: 4 }}>
                      {a.code && <span style={{ fontFamily: 'monospace', marginRight: 8 }}>{a.code}</span>}
                      {a.label}
                    </TableCell>
                    <TableCell align="right">{a.solde.toFixed(2)} €</TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell sx={{ pl: 4 }}><em>Sous-total {g.label.toLowerCase()}</em></TableCell>
                  <TableCell align="right"><em>{g.total.toFixed(2)} €</em></TableCell>
                </TableRow>
              </React.Fragment>
            )
          ))}
          {groups.every((g) => g.accounts.length === 0) && (
            <TableRow>
              <TableCell colSpan={2} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                Aucune donnée pour cet exercice.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      <Divider />
      <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', bgcolor: 'grey.100' }}>
        <Typography sx={{ fontWeight: 'bold' }}>TOTAL {title.toUpperCase()}</Typography>
        <Typography sx={{ fontWeight: 'bold' }}>{total.toFixed(2)} €</Typography>
      </Box>
    </TableContainer>
  );

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={() => navigate('/accounting')} aria-label="Retour">
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" sx={{ fontWeight: 'bold' }}>Bilan</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Chip
            label={isBalanced ? 'Actif = Passif ✓' : `Écart : ${(totalActif - totalPassif).toFixed(2)} €`}
            color={isBalanced ? 'success' : 'error'}
          />
          <TextField select size="small" label="Exercice (au 31/12)" value={year} onChange={(e) => setYear(e.target.value)} sx={{ minWidth: 180 }}>
            {years.map((y) => (
              <MenuItem key={y} value={y}>{y}</MenuItem>
            ))}
          </TextField>
        </Box>
      </Box>

      {!loading && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            {renderSection('Actif', actifRows, totalActif)}
          </Grid>
          <Grid item xs={12} md={6}>
            {renderSection('Passif', passifRows, totalPassif)}
          </Grid>
        </Grid>
      )}

      <Paper sx={{ p: 2, mt: 3 }}>
        <Typography variant="body2" color="text.secondary">
          Résultat de l'exercice {year} (produits − charges, uniquement les écritures datées dans cette année) :{' '}
          <strong style={{ color: resultatExercice >= 0 ? '#10B981' : '#EF4444' }}>
            {resultatExercice >= 0 ? '+' : ''}{resultatExercice.toFixed(2)} €
          </strong>
        </Typography>
      </Paper>
    </Container>
  );
};

export default BalanceSheet;
